import React from 'react';
import { Network, Wifi, Camera, Router, ArrowRight, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export const ProduitsUbiquiti = () => {
  const categories = [
    {
      icon: <Wifi className="w-6 h-6" />,
      title: 'UniFi Networking',
      description: 'Points d\'accès WiFi, switches et routeurs professionnels',
      products: ['U6+', 'U6 Pro', 'U7 Pro', 'Dream Machine'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: 'UniFi Protect',
      description: 'Systèmes de vidéosurveillance IP haute définition',
      products: ['G4 Bullet', 'G5 Pro', 'AI 360', 'AI Theta'],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: <Router className="w-6 h-6" />,
      title: 'EdgeMAX',
      description: 'Routeurs et switches pour infrastructures réseau avancées',
      products: ['EdgeRouter X', 'ER-4', 'EdgeSwitch'],
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: <Network className="w-6 h-6" />,
      title: 'UniFi Connect',
      description: 'Solutions de communication et accès visiteurs',
      products: ['Talk', 'Access', 'Intercom'],
      color: 'from-green-500 to-emerald-500'
    }
  ];

  const stats = [
    { value: '1300+', label: 'Produits disponibles' },
    { value: '24/7', label: 'Support technique' },
    { value: '100%', label: 'Produits authentiques' }
  ];

  return (
    <section className="py-24 relative overflow-hidden bg-gradient-to-b from-[#0A0A0A] to-[#050505]">
      {/* Background Effects */}
      <div className="absolute inset-0 network-lines opacity-5"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500 opacity-10 blur-[150px] rounded-full"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#FF6B35] opacity-10 blur-[150px] rounded-full"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35] mb-4 block">
            Solutions Ubiquiti
          </span>
          <h2 className="text-4xl sm:text-5xl font-black mb-6 tracking-tight">
            Équipements <span className="text-[#FF6B35]">Réseau</span> Professionnels
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Découvrez notre gamme complète de produits Ubiquiti : WiFi, caméras IP, routeurs et switches pour des infrastructures réseau performantes et fiables.
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-6 mb-12">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center glass rounded-xl p-6 border border-white/10"
            >
              <div className="text-3xl font-black text-[#FF6B35] mb-2">{stat.value}</div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {categories.map((category, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#FF6B35]/50 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(255,107,53,0.2)]"
            >
              <div className={`w-14 h-14 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center mb-4 text-white shadow-lg group-hover:scale-110 transition-transform`}>
                {category.icon}
              </div>
              <h3 className="text-xl font-bold mb-2 group-hover:text-[#FF6B35] transition-colors">
                {category.title}
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                {category.description}
              </p>
              <div className="space-y-1">
                {category.products.map((product, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="w-1 h-1 bg-[#FF6B35] rounded-full"></div>
                    {product}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-8 border-2 border-[#FF6B35]/30 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-transparent to-[#FF6B35]/10"></div>
          
          <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-8 h-8 text-[#FF6B35]" />
                <h3 className="text-2xl font-bold">Catalogue Complet Disponible</h3>
              </div>
              <p className="text-gray-400 mb-6">
                Explorez notre catalogue de plus de 1300 produits Ubiquiti. 
                Notre équipe certifiée vous accompagne dans le choix des solutions adaptées à vos besoins.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/produits-ubiquiti"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF6B35] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)]"
                >
                  Voir le catalogue
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:border-[#FF6B35] hover:text-[#FF6B35] transition-all"
                >
                  Demander une soumission
                </button>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <img 
                src="/images/partners/ubiquiti-horizontal-light.png" 
                alt="Ubiquiti" 
                className="w-full max-w-xs opacity-80"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
